import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";
import { authHeader } from "./helpers/auth-header";
import { BS_BASE } from "./configs/api";

const HOURS_START = 7;  // 07:00
const HOURS_END = 20;   // 20:00
const PX_PER_HOUR = 30; // 25% smaller (was 60)
const PX_PER_MIN = PX_PER_HOUR / 60; // pixels per minute

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function dmy(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}
function startOfWeekMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay(); // 0=Sun..6=Sat
  const delta = (day === 0 ? -6 : 1 - day); // move to Monday
  dt.setDate(dt.getDate() + delta);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function addDays(d, n) {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + n);
  return nd;
}
function parseTimeToMinutes(t) {
  // supports "HH:mm:ss" or "HH:mm"
  if (!t) return 0;
  const parts = String(t).split(":");
  const hh = parseInt(parts[0] || "0", 10);
  const mm = parseInt(parts[1] || "0", 10);
  return hh * 60 + mm;
}
function durationMinutes(start, end) {
  return Math.max(15, parseTimeToMinutes(end) - parseTimeToMinutes(start)); // min 15 min height
}
function statusColor(accepted, type) {
  // color by status/type
  if (accepted === false) return "#f59e0b"; // amber for unconfirmed
  if (type === 1) return "#2e7d32";        // green for school
  return "#1976d2";                        // blue for confirmed lessons
}
function statusBorder(accepted, type) {
  return "2px solid #000"; // black outline for all bookings
}
function typeLabel(type) {
  return type === 1 ? "School" : "Lesson";
}
function ymdToDmy(ymd) {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

export default function ViewBookings() {
  const [anchorDate, setAnchorDate] = useState(() => startOfWeekMonday(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isSchool, setIsSchool] = useState(false); // NEW: toggle School vs Lesson

  // NEW: get current user and type/id
  const { userType, myId } = React.useMemo(() => {
    try {
      const stored = localStorage.getItem("currentUser");
      const cu = stored ? JSON.parse(stored) : null;
      const ut = Number(cu?.userType ?? cu?.type ?? cu?.user?.userType ?? NaN);
      const id = cu?.id ?? cu?.userId ?? cu?.user?.id ?? null;
      return { userType: ut, myId: id };
    } catch {
      return { userType: undefined, myId: null };
    }
  }, []);

  const weekDays = useMemo(() => {
    // 7 days starting Monday
    return Array.from({ length: 7 }, (_, i) => addDays(anchorDate, i));
  }, [anchorDate]);

  // Always show all 7 days
  // const visibleDays = useMemo(() => {
  //   return workWeek ? weekDays.slice(0, 5) : weekDays; // Mon–Fri or Mon–Sun
  // }, [weekDays, workWeek]);
  const visibleDays = useMemo(() => weekDays, [weekDays]);

  const range = useMemo(() => {
    const from = ymd(weekDays[0]);
    const to = ymd(weekDays[6]);
    return { from, to };
  }, [weekDays]);

  useEffect(() => {
    async function fetchWeek() {
      setLoading(true);
      setError("");
      try {
        // Base URL
        let url = `${BS_BASE}/bookings-by-range?StartDate=${encodeURIComponent(dmy(weekDays[0]))}&EndDate=${encodeURIComponent(dmy(weekDays[6]))}&isSchool=${isSchool}`;
        // NEW: if userType === 27, only fetch this user's bookings (server-side filter)
        if (userType === 27 && myId) {
          url += `&clientId=${encodeURIComponent(myId)}`;
        }

        const res = await fetch(url, { headers: { Accept: "application/json", ...authHeader() } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

        const normalized = list.map((b) => ({
          id: b.id,
          date: b.date,
          day: b.day,
          start: String(b.startTime || "").slice(0, 5),
          end: String(b.endTime || "").slice(0, 5),
          accepted: b.accepted,
          type: b.type,
          firstName: b.firstName,
          lastName: b.lastName,
          // NEW: keep potential owner/client identifiers for client-side filtering fallback
          ownerId: b.clientId ?? b.userId ?? b.customerId ?? b.studentId
        }));

        // Keep school/lesson toggle
        const typeFiltered = normalized.filter((b) => (isSchool ? b.type === 1 : b.type !== 1));

        // NEW: if userType === 27, ensure client-side filter as fallback
        const final = (userType === 27 && myId)
          ? typeFiltered.filter((b) => String(b.ownerId) === String(myId))
          : typeFiltered;

        setItems(final);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
    fetchWeek();
  }, [weekDays, isSchool, userType, myId]); // include userType/myId

  const hours = useMemo(() => {
    const arr = [];
    for (let h = HOURS_START; h <= HOURS_END; h++) {
      arr.push(`${String(h).padStart(2, "0")}:00`);
    }
    return arr;
  }, []);

  const heightPx = (HOURS_END - HOURS_START) * PX_PER_HOUR;

  function goPrevWeek() {
    setAnchorDate(addDays(anchorDate, -7));
  }
  function goNextWeek() {
    setAnchorDate(addDays(anchorDate, 7));
  }
  function goToday() {
    setAnchorDate(startOfWeekMonday(new Date()));
  }

  // Add: cancel handler
  const handleCancelBooking = async (booking) => {
    if (!booking) return;
    const ok = window.confirm(`Cancel booking for ${booking.start}${booking.end ? `–${booking.end}` : ""} on ${ymdToDmy(booking.date)}?`);
    if (!ok) return;
    try {
      // Adjust endpoint/query to your API
      const res = await fetch(`${BS_BASE}/cancel-booking?id=${encodeURIComponent(booking.id)}`, {
        method: "POST",
        headers: { Accept: "application/json", ...authHeader() }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Optimistically update UI
      setItems(prev => prev.filter(i => i.id !== booking.id));
      setSelected(null);
    } catch (e) {
      alert(`Failed to cancel: ${e instanceof Error ? e.message : e}`);
    }
  };

  return (
    <div className="bookingform-page" style={{ minHeight: "100vh" }}>
      <div className="bookingform-form main-container" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Header onBack={() => window.history.back()} />

        {/* Put Bookings heading on its own line */}
        <h2 style={{ margin: "-8px 0 2px" }}>{loading ? "Loading…" : "Bookings"}</h2>

        {/* Controls row (toggle + week nav) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8
          }}
        >
          <div role="group" aria-label="Booking type" style={{ display: "inline-flex", border: "1px solid #ddd", borderRadius: 8, overflow: "hidden" }}>
            <button
              onClick={() => setIsSchool(false)}
              style={{
                padding: "6px 10px",
                fontWeight: 700,
                background: isSchool ? "#f7f7f7" : "#1976d2",
                color: isSchool ? "#333" : "#fff",
                border: "none",
                cursor: "pointer"
              }}
            >
              Lesson
            </button>
            <button
              onClick={() => setIsSchool(true)}
              style={{
                padding: "6px 10px",
                fontWeight: 700,
                background: isSchool ? "#1976d2" : "#f7f7f7",
                color: isSchool ? "#fff" : "#333",
                borderLeft: "1px solid #ddd",
                borderRight: "none",
                borderTop: "none",
                borderBottom: "none",
                cursor: "pointer"
              }}
            >
              School
            </button>
          </div>

          {/* align nav buttons to the right */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={goPrevWeek} style={navBtnStyle}>◀</button>
            <button onClick={goToday} style={navBtnStyle}>Today</button>
            <button onClick={goNextWeek} style={navBtnStyle}>▶</button>
          </div>
        </div>
        <div style={{ color: "#666", marginTop: -6 }}>
          {dmy(visibleDays[0])} – {dmy(visibleDays[visibleDays.length - 1])}
        </div>

        {error && (
          <div style={{ color: "crimson", fontSize: "0.9rem" }}>
            Error: {error}
          </div>
        )}

        {/* Scrollable calendar area */}
        <div
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            border: "1px solid #e5e5e5",
            background: "#e5e5e5",
            borderRadius: 10
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `56px repeat(${visibleDays.length}, 1fr)`,
              gap: 1,
            }}
          >
            {/* Header row */}
            <div style={{ background: "#fafafa" }} />
            {visibleDays.map((d) => (
              <div
                key={`h-${d.toDateString()}`}
                style={{
                  background: "#fafafa",
                  padding: "8px 10px",
                  fontWeight: 700,
                  fontSize: 12
                }}
              >
                {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}
              </div>
            ))}

            {/* Time + day columns */}
            <div style={{ background: "#fff", position: "relative" }}>
              {/* time labels */}
              <div style={{ position: "relative", height: heightPx }}>
                {hours.map((h, idx) => (
                  <div
                    key={h}
                    style={{
                      position: "absolute",
                      top: idx * PX_PER_HOUR - 8,
                      right: 4,
                      color: "#666",
                      fontSize: 11,
                      textAlign: "right",
                      width: "100%"
                    }}
                  >
                    {h}
                  </div>
                ))}
              </div>
            </div>

            {visibleDays.map((d) => {
              const key = ymd(d);
              const dayItems = items.filter((i) => i.date === key);
              return (
                <div key={key} style={{ background: "#fff", position: "relative", height: heightPx }}>
                  {/* hour lines */}
                  {hours.map((h, idx) => (
                    <div
                      key={`${key}-${h}`}
                      style={{
                        position: "absolute",
                        top: idx * PX_PER_HOUR,
                        left: 0,
                        right: 0,
                        height: 1,
                        background: "#eee"
                      }}
                    />
                  ))}
                  {/* bookings */}
                  {dayItems.map((b) => {
                    const topPx = (parseTimeToMinutes(b.start) - HOURS_START * 60) * PX_PER_MIN;
                    const hPx = durationMinutes(b.start, b.end) * PX_PER_MIN;
                    return (
                      <div
                        key={b.id}
                        onClick={() => setSelected(b)}
                        title={`${b.firstName || ""} ${b.lastName || ""} ${b.start}${b.end ? `–${b.end}` : ""}`}
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: topPx,
                          height: hPx,
                          minHeight: 18,               // was 24 (25% smaller)
                          background: statusColor(b.accepted, b.type),
                          border: statusBorder(b.accepted, b.type),
                          borderRadius: 0,
                          boxShadow: "none",
                          padding: 0,
                          overflow: "hidden",
                          zIndex: 2,
                          cursor: "pointer"
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {selected && (
          <SlotDetailsModal
            booking={selected}
            onClose={() => setSelected(null)}
            onCancel={() => handleCancelBooking(selected)} // NEW: pass cancel action
          />
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 6, color: "#555", fontSize: 12 }}>
          <span><span style={legendBox("#1976d2")} /> Confirmed</span>
          <span><span style={legendBox("#f59e0b")} /> Unconfirmed</span>
          <span><span style={legendBox("#2e7d32")} /> School</span>
        </div>
      </div>
    </div>
  );
}

const navBtnStyle = {
  background: "#f4f4f4",
  color: "#333",
  border: "1px solid #ddd",
  borderRadius: 6,
  padding: "6px 10px",
  fontWeight: 700,
  cursor: "pointer"
};

function legendBox(color) {
  return {
    display: "inline-block",
    width: 10,
    height: 10,
    background: color,
    borderRadius: 2,
    marginRight: 6,
    verticalAlign: "middle"
  };
}

function SlotDetailsModal({ booking, onClose, onCancel }) {
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(390px, 69vw)",
          background: "#fff",
          borderRadius: 10,
          padding: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)"
        }}
      >
        {/* Center the title horizontally within the modal */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20, position: "relative" }}>
          <h3
            style={{
              margin: 0,
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              textAlign: "center",
              pointerEvents: "none",
              color: "#04192eff"
            }}
          >
            Booking Details
          </h3>
          {/* Removed top-right "×" close button */}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: 6, columnGap: 10, fontSize: 14 }}>
          <div style={{ color: "#071320ff" }}>Date</div>
          <div style={{ color: "#071320ff" }}>{ymdToDmy(booking.date)}</div>

          <div style={{ color: "#071320ff" }}>Time</div>
          <div style={{ color: "#071320ff" }}>{booking.start}{booking.end ? `–${booking.end}` : ""}</div>

          <div style={{ color: "#071320ff" }}>Type</div>
          <div style={{ color: "#071320ff" }}>{typeLabel(booking.type)}</div>

          <div style={{ color: "#071320ff" }}>Status</div>
          <div style={{ color: "#071320ff" }}>{booking.accepted ? "Confirmed" : "Unconfirmed"}</div>

          {(booking.firstName || booking.lastName) && (
            <>
              <div style={{ color: "#071320ff" }}>Customer</div>
              <div style={{ color: "#071320ff" }}>{[booking.firstName, booking.lastName].filter(Boolean).join(" ")}</div>
            </>
          )}
        </div>

        <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              background: "#d93025",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 14px",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Cancel Slot
          </button>
          <button
            onClick={onClose}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 14px",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}